import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import OperatorPageTip from "@/components/OperatorPageTip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Users,
  Phone,
  EnvelopeSimple,
  Buildings,
  PencilSimple,
  MagnifyingGlass,
  X as XIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";

function clientLabel(c) {
  // Iter209dz6 — Per clienti azienda mostra la Ragione sociale, altrimenti Nome Cognome.
  if (c.owner_type === "azienda" && (c.owner_company_name || "").trim()) {
    return c.owner_company_name.trim();
  }
  return (
    [c.first_name, c.last_name].filter(Boolean).join(" ").trim() ||
    c.owner_company_name ||
    c.email ||
    c.phone ||
    "—"
  );
}

function ClientCard({ client, onOpen }) {
  return (
    <button
      type="button"
      onClick={() => onOpen(client)}
      className="text-left rounded-xl border border-slate-200 bg-white p-4 hover:border-purple-400 hover:shadow-md transition-all flex items-center gap-4 group"
      data-testid={`client-card-${client.id}`}
    >
      <div className="w-11 h-11 rounded-full bg-gradient-to-br from-purple-500 to-purple-700 text-white flex items-center justify-center text-lg font-semibold shrink-0">
        {(client.owner_company_name || client.first_name || client.last_name || client.email || "?")[0]?.toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-slate-800 truncate">{clientLabel(client)}</div>
        <div className="text-xs text-slate-500 flex items-center gap-3 mt-1 flex-wrap">
          {client.phone && (
            <span className="flex items-center gap-1">
              <Phone size={11} /> {client.phone}
            </span>
          )}
          {client.email && (
            <span className="flex items-center gap-1 truncate">
              <EnvelopeSimple size={11} /> {client.email}
            </span>
          )}
        </div>
      </div>
      <div className="flex flex-col items-end gap-1 shrink-0">
        <span
          className="inline-flex items-center gap-1 text-xs font-medium text-purple-800 bg-purple-100 rounded-full px-2 py-0.5"
          data-testid={`client-properties-count-${client.id}`}
        >
          <Buildings size={11} /> {client.properties_count || 0}
        </span>
        <span className="text-[11px] text-slate-400 group-hover:text-purple-600 flex items-center gap-1">
          <PencilSimple size={10} /> Modifica
        </span>
      </div>
    </button>
  );
}

function ClientModal({ client, open, onOpenChange, onSaved }) {
  const [full, setFull] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    first_name: "", last_name: "", phone: "", email: "",
    owner_type: "privato",
    owner_vat_number: "", owner_company_name: "",
    owner_company_fiscal_code: "", owner_company_address: "",
  });
  const [saving, setSaving] = useState(false);
  // Iter209dz6 — Lookup VIES per clienti azienda (stesso pattern di NewPropertyForm).
  const [vatLoading, setVatLoading] = useState(false);
  const [vatError, setVatError] = useState("");
  const [vatDone, setVatDone] = useState(false);
  const vatTimerRef = useRef(null);
  const vatLastRef = useRef("");
  // Iter209s — Errore fetch cliente: consente al render di distinguere
  // "sto caricando" da "il caricamento è fallito" così la sezione
  // Immobili non rimane bloccata sullo spinner in caso di errore.
  const [error, setError] = useState(null);
  // Iter209dzn — "+ Dettagli" per nascondere Telefono + Email di default
  // (stessa UX di NewPropertyForm.jsx Iter209dz8). Si auto-espande se
  // il cliente in edit ha già un telefono o email salvati.
  const [contactsExpanded, setContactsExpanded] = useState(false);
  useEffect(() => {
    if (!contactsExpanded &&
        ((form.phone || "").trim() || (form.email || "").trim())) {
      setContactsExpanded(true);
    }
  }, [form.phone, form.email, contactsExpanded]);

  useEffect(() => {
    if (!open || !client) return;
    setEditing(false);
    setFull(null);
    setError(null);
    setVatError("");
    setVatDone(false);
    api
      .get(`/clients/${client.id}`)
      .then((r) => {
        setFull(r.data);
        setForm({
          first_name: r.data.first_name || "",
          last_name: r.data.last_name || "",
          phone: r.data.phone || "",
          email: r.data.email || "",
          owner_type: r.data.owner_type || "privato",
          owner_vat_number: r.data.owner_vat_number || "",
          owner_company_name: r.data.owner_company_name || "",
          owner_company_fiscal_code: r.data.owner_company_fiscal_code || "",
          owner_company_address: r.data.owner_company_address || "",
        });
      })
      .catch((e) => {
        setError(e?.response?.data?.detail || "load_failed");
      });
  }, [open, client]);

  const patchForm = (patch) => setForm((f) => ({ ...f, ...patch }));

  const runVatLookup = async (vatRaw) => {
    const vat = (vatRaw ?? form.owner_vat_number ?? "").replace(/\s/g, "").trim();
    if (!vat) { setVatError("Inserisci la P.IVA prima di cercare"); return; }
    setVatLoading(true); setVatError(""); setVatDone(false);
    try {
      const { data } = await api.get(`/vat/lookup`, { params: { vat } });
      if (!data?.found || !data?.company) {
        setVatError("P.IVA non trovata — inserisci i dati manualmente");
        return;
      }
      const c = data.company;
      patchForm({
        owner_company_name: c.name || form.owner_company_name,
        owner_company_address: c.address || "",
      });
      setVatDone(true);
      toast.success(`Dati aziendali caricati: ${c.name}`);
    } catch (_e) {
      setVatError("P.IVA non trovata — inserisci i dati manualmente");
    } finally { setVatLoading(false); }
  };

  // Auto-lookup VIES al raggiungimento di 11 cifre (debounce 600ms).
  useEffect(() => {
    const raw = (form.owner_vat_number || "").replace(/\D/g, "");
    if (vatTimerRef.current) clearTimeout(vatTimerRef.current);
    if (!editing) return;
    if (form.owner_type !== "azienda") return;
    if (raw.length !== 11) { setVatDone(false); return; }
    if (raw === vatLastRef.current) return;
    vatTimerRef.current = setTimeout(() => {
      vatLastRef.current = raw;
      runVatLookup(raw);
    }, 600);
    return () => { if (vatTimerRef.current) clearTimeout(vatTimerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.owner_vat_number, form.owner_type, editing]);

  const save = async () => {
    if (form.owner_type === "azienda") {
      if (!form.owner_vat_number.trim()) {
        toast.error("P.IVA obbligatoria per clienti azienda");
        return;
      }
      if (!form.owner_company_name.trim()) {
        toast.error("Ragione sociale obbligatoria per clienti azienda");
        return;
      }
    }
    setSaving(true);
    try {
      const r = await api.patch(`/clients/${client.id}`, form);
      setFull(r.data);
      setEditing(false);
      toast.success("Anagrafica aggiornata sugli immobili collegati");
      onSaved?.(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const c = full || client;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="client-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users size={20} weight="fill" /> Scheda cliente
          </DialogTitle>
        </DialogHeader>

        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-wide font-semibold text-slate-500">
              Anagrafica
            </div>
            {!editing ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setEditing(true)}
                data-testid="client-edit-btn"
              >
                <PencilSimple size={13} /> Modifica
              </Button>
            ) : (
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setEditing(false)}
                  disabled={saving}
                >
                  <XIcon size={13} /> Annulla
                </Button>
                <Button
                  size="sm"
                  className="ht-gradient border-0"
                  onClick={save}
                  disabled={saving}
                  data-testid="client-save-btn"
                >
                  {saving ? "Salvo…" : "Salva"}
                </Button>
              </div>
            )}
          </div>

          {!editing ? (
            // ============ DISPLAY MODE ============
            c.owner_type === "azienda" ? (
              <div className="grid grid-cols-2 gap-3 text-sm" data-testid="client-display-azienda">
                <div className="col-span-2">
                  <div className="text-xs text-slate-500">Ragione sociale</div>
                  <div className="font-medium">{c.owner_company_name || "—"}</div>
                </div>
                <div className="col-span-2">
                  <div className="text-xs text-slate-500">P.IVA</div>
                  <div className="font-medium">{c.owner_vat_number || "—"}</div>
                </div>
                <div className="col-span-2">
                  <div className="text-xs text-slate-500">Indirizzo sede legale</div>
                  <div className="font-medium">{c.owner_company_address || "—"}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Telefono</div>
                  <div className="font-medium">{c.phone || "—"}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Email</div>
                  <div className="font-medium truncate">{c.email || "—"}</div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3 text-sm" data-testid="client-display-privato">
                <div>
                  <div className="text-xs text-slate-500">Nome</div>
                  <div className="font-medium">{c.first_name || "—"}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Cognome</div>
                  <div className="font-medium">{c.last_name || "—"}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Telefono</div>
                  <div className="font-medium">{c.phone || "—"}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Email</div>
                  <div className="font-medium truncate">{c.email || "—"}</div>
                </div>
              </div>
            )
          ) : (
            // ============ EDIT MODE ============
            <div className="space-y-3">
              <div data-testid="client-owner-type">
                <Label>Tipo cliente *</Label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  {[
                    { v: "privato", label: "Privato" },
                    { v: "azienda", label: "P.IVA" },
                  ].map((opt) => (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => patchForm({ owner_type: opt.v })}
                      className={
                        "border-2 rounded-md py-2 text-sm font-medium transition-all " +
                        (form.owner_type === opt.v
                          ? "border-purple-500 bg-purple-50 text-purple-800"
                          : "border-slate-200 bg-white text-slate-700 hover:border-purple-300")
                      }
                      data-testid={`client-owner-type-${opt.v}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {form.owner_type === "azienda" ? (
                <div className="space-y-3" data-testid="client-azienda-block">
                  <div>
                    <Label>P.IVA *</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        value={form.owner_vat_number}
                        onChange={(e) => patchForm({ owner_vat_number: e.target.value })}
                        placeholder="12345678901"
                        inputMode="numeric"
                        data-testid="client-vat"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => runVatLookup()}
                        disabled={vatLoading}
                        data-testid="client-vat-lookup"
                      >
                        {vatLoading ? "Ricerca…" : "Verifica P.IVA"}
                      </Button>
                    </div>
                    {vatError && (
                      <div className="text-xs text-red-600 mt-1" data-testid="client-vat-lookup-error">
                        {vatError}
                      </div>
                    )}
                    {vatDone && !vatError && (
                      <div className="text-xs text-green-700 mt-1" data-testid="client-vat-lookup-success">
                        Dati aziendali autocompilati da VIES.
                      </div>
                    )}
                    <p className="text-[10px] text-muted-foreground mt-1">
                      Alla digitazione di 11 cifre interroghiamo VIES (API UE) e compiliamo automaticamente Ragione sociale e Indirizzo.
                    </p>
                  </div>
                  <div>
                    <Label>Ragione sociale *</Label>
                    <Input
                      value={form.owner_company_name}
                      onChange={(e) => patchForm({ owner_company_name: e.target.value })}
                      placeholder="Immobiliare Rossi SRL"
                      data-testid="client-company-name"
                    />
                  </div>
                  {/* Iter209dzk — Rimosso il campo "Codice Fiscale azienda"
                      dalla scheda cliente (display + edit) su richiesta.
                      Il valore eventualmente già presente in DB viene
                      preservato: resta nello state del form e viene
                      inviato invariato nel PATCH. */}
                  <div>
                    <Label>Indirizzo sede legale</Label>
                    <Input
                      value={form.owner_company_address}
                      onChange={(e) => patchForm({ owner_company_address: e.target.value })}
                      placeholder="Via Milano 12, 20100 Milano (MI)"
                      data-testid="client-company-address"
                    />
                  </div>
                  <div className="pt-1">
                    <button
                      type="button"
                      onClick={() => setContactsExpanded((v) => !v)}
                      className="text-xs font-medium text-purple-700 hover:text-purple-900 inline-flex items-center gap-1"
                      data-testid="client-contacts-toggle"
                    >
                      {contactsExpanded ? "− Dettagli" : "+ Dettagli"}
                    </button>
                  </div>
                  {contactsExpanded && (
                    <div className="grid grid-cols-2 gap-3" data-testid="client-contacts-fields">
                      <div>
                        <Label>Telefono</Label>
                        <Input
                          value={form.phone}
                          onChange={(e) => patchForm({ phone: e.target.value })}
                          data-testid="client-input-phone"
                        />
                      </div>
                      <div>
                        <Label>Email</Label>
                        <Input
                          type="email"
                          value={form.email}
                          onChange={(e) => patchForm({ email: e.target.value })}
                          data-testid="client-input-email"
                        />
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-3" data-testid="client-privato-block">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Nome</Label>
                      <Input
                        value={form.first_name}
                        onChange={(e) => patchForm({ first_name: e.target.value })}
                        data-testid="client-input-first-name"
                      />
                    </div>
                    <div>
                      <Label>Cognome</Label>
                      <Input
                        value={form.last_name}
                        onChange={(e) => patchForm({ last_name: e.target.value })}
                        data-testid="client-input-last-name"
                      />
                    </div>
                  </div>
                  <div>
                    <button
                      type="button"
                      onClick={() => setContactsExpanded((v) => !v)}
                      className="text-xs font-medium text-purple-700 hover:text-purple-900 inline-flex items-center gap-1"
                      data-testid="client-contacts-toggle"
                    >
                      {contactsExpanded ? "− Dettagli" : "+ Dettagli"}
                    </button>
                  </div>
                  {contactsExpanded && (
                    <div className="grid grid-cols-2 gap-3" data-testid="client-contacts-fields">
                      <div>
                        <Label>Telefono</Label>
                        <Input
                          value={form.phone}
                          onChange={(e) => patchForm({ phone: e.target.value })}
                          data-testid="client-input-phone"
                        />
                      </div>
                      <div>
                        <Label>Email</Label>
                        <Input
                          type="email"
                          value={form.email}
                          onChange={(e) => patchForm({ email: e.target.value })}
                          data-testid="client-input-email"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
              <p className="text-[11px] text-slate-500 italic">
                Le modifiche vengono propagate a tutti gli immobili collegati.
              </p>
            </div>
          )}
        </div>

        <div className="mt-4">
          <div className="text-xs uppercase tracking-wide font-semibold text-slate-500 mb-2 flex items-center gap-1.5">
            <Buildings size={13} /> Immobili collegati ({(full?.properties || []).length})
          </div>
          {!full && !error && (
            <div className="text-xs text-slate-500 italic" data-testid="client-properties-loading">Caricamento…</div>
          )}
          {error && (
            <div
              className="text-xs text-red-600 italic border border-red-200 bg-red-50 rounded p-2"
              data-testid="client-properties-error"
            >
              Impossibile caricare i dati del cliente.
            </div>
          )}
          {full?.properties?.length > 0 && (
            <div className="space-y-2" data-testid="client-properties-list">
              {full.properties.map((p) => (
                <Link
                  key={p.id}
                  to={`/properties/${p.id}`}
                  className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-2.5 hover:border-purple-400 hover:bg-purple-50 transition-colors"
                  data-testid={`client-property-${p.id}`}
                >
                  <div className="w-10 h-10 rounded bg-slate-100 flex items-center justify-center shrink-0">
                    {p.cover_image_url ? (
                      <img src={p.cover_image_url} alt="" className="w-full h-full object-cover rounded" />
                    ) : (
                      <Buildings size={16} className="text-slate-500" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">
                      {p.title || p.address || "Immobile"}
                    </div>
                    <div className="text-xs text-slate-500 truncate">
                      {p.address}
                      {p.city ? `, ${p.city}` : ""}
                    </div>
                  </div>
                  <span
                    className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full font-semibold ${
                      p.is_archived ? "bg-slate-200 text-slate-600" : "bg-green-100 text-green-700"
                    }`}
                  >
                    {p.status}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [openClient, setOpenClient] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/clients");
      setClients(r.data?.items || []);
    } catch {
      toast.error("Errore caricamento clienti");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return clients;
    return clients.filter((c) => {
      const hay = [c.first_name, c.last_name, c.email, c.phone].join(" ").toLowerCase();
      return hay.includes(query);
    });
  }, [q, clients]);

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="clients-page">
      <OperatorPageTip
        pageKey="clienti"
        title="Anagrafica clienti"
        body="Qui trovi tutti i clienti collegati alle pratiche che stai lavorando. Cliccando un cliente vedi immobili, storico pratiche e contatti."
      />
      <div className="mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Users size={24} weight="fill" /> Clienti
        </h1>
        <p className="text-sm text-slate-500">
          Rubrica dei clienti collegati ai tuoi immobili. Per aggiungere o modificare un cliente, apri la scheda del suo immobile.
        </p>
      </div>

      <div className="relative mb-4 max-w-sm">
        <MagnifyingGlass
          size={14}
          className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"
        />
        <Input
          placeholder="Cerca nome, email, telefono…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="pl-8"
          data-testid="clients-search"
        />
      </div>

      {loading && <div className="text-sm text-slate-500">Caricamento…</div>}
      {!loading && filtered.length === 0 && (
        <div
          className="text-center py-16 border border-dashed rounded-lg text-slate-500"
          data-testid="clients-empty"
        >
          <Users size={36} className="mx-auto mb-3 text-slate-300" />
          {clients.length === 0
            ? "Nessun cliente ancora. I clienti compaiono automaticamente quando aggiungi un immobile con i dati proprietario."
            : "Nessun cliente corrisponde alla ricerca."}
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="grid gap-3 md:grid-cols-2" data-testid="clients-grid">
          {filtered.map((c) => (
            <ClientCard key={c.id} client={c} onOpen={setOpenClient} />
          ))}
        </div>
      )}

      {openClient && (
        <ClientModal
          client={openClient}
          open={!!openClient}
          onOpenChange={(o) => !o && setOpenClient(null)}
          onSaved={() => load()}
        />
      )}
    </div>
  );
}
